import React, { useCallback } from 'react';
import { Port, Shipper } from '../../types';
import { portService, shipperService } from '../../services';
import { RequiredIndicator } from '../ui';
import { AsyncSearchSelect } from '../forms';

interface OrderDataSectionProps {
  portId: string;
  shipperId: string;
  bookingNumber: string;
  onPortChange: (portId: string, displayLabel?: string) => void;
  onShipperChange: (shipperId: string, displayLabel?: string) => void;
  onBookingNumberChange: (bookingNumber: string) => void;
  disabled?: boolean;
  initialPortDisplay?: string;
  initialShipperDisplay?: string;
}

export const OrderDataSection: React.FC<OrderDataSectionProps> = ({
  portId,
  shipperId,
  bookingNumber,
  onPortChange,
  onShipperChange,
  onBookingNumberChange,
  disabled = false,
  initialPortDisplay = '',
  initialShipperDisplay = '',
}) => {
  // Load ports from backend
  const loadPorts = useCallback(async (query: string) => {
    const results = await portService.getAll({ query, limit: 20 });
    return results;
  }, []);

  // Load shippers from backend
  const loadShippers = useCallback(async (query: string) => {
    const results = await shipperService.getAll({ query, limit: 20 });
    return results;
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1.5 pb-1 border-b border-gray-200 dark:border-gray-700 flex items-center">
        <svg
          className="w-3.5 h-3.5 mr-1.5 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        Order Data
      </h3>

      <div className="space-y-1.5">
        <AsyncSearchSelect<Port>
          loadOptions={loadPorts}
          getValue={(port) => port.id}
          getLabel={(port) => `${port.port} (${port.code})`}
          getSecondaryLabel={(port) => `${port.code} - ${port.country}`}
          onSelect={(port) => onPortChange(port.id, `${port.port} (${port.code})`)}
          value={portId}
          initialDisplayValue={initialPortDisplay}
          label={
            <>
              <span>POD (Port of Discharge)</span> <RequiredIndicator />
            </>
          }
          placeholder="Search port..."
          disabled={disabled}
          noOptionsMessage="No ports found"
        />

        <AsyncSearchSelect<Shipper>
          loadOptions={loadShippers}
          getValue={(shipper) => shipper.id}
          getLabel={(shipper) => `${shipper.code} - ${shipper.shortname || shipper.name}`}
          getSecondaryLabel={(shipper) => shipper.name}
          onSelect={(shipper) => onShipperChange(shipper.id, `${shipper.code} - ${shipper.shortname || shipper.name}`)}
          value={shipperId}
          initialDisplayValue={initialShipperDisplay}
          label={
            <>
              <span>Shipper No.</span> <RequiredIndicator />
            </>
          }
          placeholder="Search shipper..."
          disabled={disabled}
          noOptionsMessage="No shippers found"
        />

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
            Booking Number
          </label>
          <input
            type="text"
            value={bookingNumber}
            onChange={(e) => onBookingNumberChange(e.target.value)}
            disabled={disabled}
            placeholder="Enter booking number..."
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
          />
        </div>
      </div>
    </div>
  );
};
